/**
 * Everything the redline code knows about bookmark markers in OOXML.
 *
 * A bookmark is a `w:bookmarkStart` with a name and an id and a matching
 * `w:bookmarkEnd` carrying only the id. The two may sit in different
 * paragraphs, at any depth, and Word moves them freely as the document is
 * edited. Nothing else in the engine, the proposal or the anchor package reads
 * those markers: every question about which span of a paragraph a set of
 * bookmarks covers is asked of this class, so the start/end pairing is decided
 * in one place.
 *
 * Which paragraph a paragraph bookmark names is decided from its start marker
 * alone (`paragraphBookmarkNamesByOwner`): bookmark ranges interleave, so an
 * end marker says nothing reliable about ownership, and Word places the start
 * of a bookmark that begins at a paragraph's first character outside the
 * paragraph as often as inside it. The same rule decides where the web mints a
 * missing paragraph bookmark, so naming and minting can never disagree.
 *
 * Offsets are accepted-text offsets taken from the `ParagraphModel` of the
 * paragraph, so a bookmark edge and the engine agree on where a character is.
 */
import type { ParagraphModel } from "./paragraph-model";
import { elementNameWithoutPrefix, PROPERTY_TAGS, TEXT_TAGS, WORDPROCESSINGML_NS, wordIdOf } from "./ooxml-names";
import { RETIRED_PARAGRAPH_BOOKMARK_PREFIXES, isParagraphBookmarkName } from "./paragraph-bookmark-naming";
import type { AcceptedTextSpan } from "../redline-engine";

/** A paragraph bookmark start seen inside a body paragraph, with how many text elements of that paragraph came before it. */
interface ParagraphBookmarkStartInsideParagraph {
    bookmarkName: string;
    textElementsBeforeStart: number;
}

/** A paragraph of the range with its model, read once for every bookmark `edgesNeedMove` asks about. */
interface ModelledParagraph {
    paragraphElement: Element;
    model: ParagraphModel;
}

export class OoxmlBookmarks {
    private readonly bookmarkNames: Set<string>;

    constructor(
        private readonly documentBodyElement: Element,
        private readonly paragraphElements: Element[],
        bookmarkNames: string[],
    ) {
        this.bookmarkNames = new Set(bookmarkNames);
    }

    /**
     * The accepted-text span the bookmarks cover inside one paragraph, or null
     * when no marker of theirs is in it.
     *
     * A `bookmarkEnd` carries only an id, and the start may live in an earlier
     * paragraph, so the ids of every start with one of our names are gathered
     * from the whole body first. A start with no end in this paragraph runs to
     * the end of the text; an end with no start begins at offset 0. Deleted
     * pieces have zero width in the model, so struck text never shifts an edge.
     */
    spanWithin(paragraph: Element, model: ParagraphModel): AcceptedTextSpan | null {
        /**
         * SCOPE
         *   This set is local to one `spanWithin` call.
         *   Collect tracked `bookmarkStart` ids under `root` so a `bookmarkEnd` in this
         *   paragraph can match a start seen earlier in document order.
         */
        const trackedBookmarkStartIdsInSubtree = (root: Element): Set<string> => {
            const trackedBookmarkStartIds = new Set<string>();
            for (const bookmarkStartElement of Array.from(root.getElementsByTagNameNS(WORDPROCESSINGML_NS, "bookmarkStart"))) {
                /**
                 * WHEN the start name is one this instance tracks
                 *   CONSEQUENCE
                 *     Add its id to the openable set.
                 *     Untracked bookmarks stay out, so their ends cannot match here.
                 */
                if (this.bookmarkNames.has(OoxmlBookmarks.nameOf(bookmarkStartElement))) trackedBookmarkStartIds.add(OoxmlBookmarks.idOf(bookmarkStartElement));
            }
            return trackedBookmarkStartIds;
        };
        /**
         * TYPE
         *   Map from each text-bearing OOXML element in this paragraph model to its piece.
         *
         * WHY
         *   The walk sees OOXML elements.
         *   The span math needs accepted-text boundaries from `model.pieces`.
         */
        const pieceByTextNode = new Map(model.pieces.map((piece) => [piece.textNode, piece] as const));
        /**
         * WHY
         *   Seed `openIds` from the whole document body.
         *   -> a `bookmarkEnd` in this paragraph can close a tracked start from an
         *      earlier paragraph.
         */
        const openIds = trackedBookmarkStartIdsInSubtree(this.documentBodyElement);
        /**
         * TYPE
         *   Accepted-text boundary of the next marker or piece seen in this paragraph.
         *
         * EXAMPLE
         *   before any piece      -> 0
         *   piece ends at 5       -> next boundary is 5
         */
        let nextAcceptedTextOffset = 0;
        let startOffset: number | null = null;
        let endOffset: number | null = null;
        /**
         * WHY
         *   Visit descendants in document order so bookmark edges land on the same
         *   accepted-text boundaries Word uses around the text.
         */
        const walkParagraphDescendantsInDocumentOrder = (node: Element): void => {
            for (const childElement of Array.from(node.children)) {
                /**
                 * TYPE
                 *   Local OOXML tag name of the current child element.
                 */
                const childLocalName = elementNameWithoutPrefix(childElement);
                /**
                 * WHEN the child is a run or paragraph property container
                 *   CONSEQUENCE
                 *     Skip it.
                 *     Formatting metadata adds no accepted text, so the current boundary stays
                 *     unchanged.
                 */
                if (PROPERTY_TAGS.has(childLocalName)) continue;
                /**
                 * WHEN the child is `bookmarkStart`
                 *   CONSEQUENCE
                 *     Open it at the current accepted-text boundary before any deeper walk.
                 */
                if (childLocalName === "bookmarkStart") {
                    /**
                     * WHEN this start name is tracked
                     *   CONSEQUENCE
                     *     Add its id to `openIds`.
                     *     A tracked bookmark that starts inside this paragraph becomes open here.
                     */
                    if (this.bookmarkNames.has(OoxmlBookmarks.nameOf(childElement))) {
                        openIds.add(OoxmlBookmarks.idOf(childElement));
                        /**
                         * CASES
                         *   - first tracked start in this paragraph   -> record this boundary
                         *   - later tracked start in this paragraph   -> keep the earlier boundary
                         *
                         * WHY
                         *   The paragraph span starts at its first tracked start, not the last one.
                         */
                        if (startOffset === null) startOffset = nextAcceptedTextOffset;
                    }
                    /**
                     * WHEN this child was handled as `bookmarkStart`
                     *   CONSEQUENCE
                     *     Do not treat the marker as text content or recurse into it.
                     */
                    continue;
                }
                /**
                 * WHEN the child is `bookmarkEnd`
                 *   CONSEQUENCE
                 *     Try to close a tracked bookmark at the current accepted-text boundary.
                 */
                if (childLocalName === "bookmarkEnd") {
                    /**
                     * WHEN this end id is currently open
                     *   CONSEQUENCE
                     *     Set `endOffset` to the current boundary.
                     *
                     * CASES
                     *   - matching end at 4, then at 9   -> final `endOffset` is 9
                     *   - end id not open                -> ignore it
                     */
                    if (openIds.has(OoxmlBookmarks.idOf(childElement))) endOffset = nextAcceptedTextOffset;
                    /**
                     * WHEN this child was handled as `bookmarkEnd`
                     *   CONSEQUENCE
                     *     Do not recurse or advance the cursor for the marker itself.
                     */
                    continue;
                }
                const textNodePiece = pieceByTextNode.get(childElement);
                /**
                 * WHEN this OOXML element maps to a paragraph-model piece
                 *   CONSEQUENCE
                 *     Advance by the piece boundary instead of recursing into the leaf.
                 */
                if (textNodePiece) {
                    nextAcceptedTextOffset = textNodePiece.endOffset;
                    /**
                     * WHEN a model piece already supplied this node's boundary
                     *   CONSEQUENCE
                     *     Skip recursion so the same accepted text is not counted twice.
                     */
                    continue;
                }
                walkParagraphDescendantsInDocumentOrder(childElement);
            }
        };
        walkParagraphDescendantsInDocumentOrder(paragraph);
        const startOffsetIsMissing = startOffset === null;
        const endOffsetIsMissing = endOffset === null;
        /**
         * WHEN this paragraph contains no tracked bookmark edge
         *   CONSEQUENCE
         *     return null
         *
         * NULL
         *   This paragraph does not participate in the tracked bookmark span.
         */
        if (startOffsetIsMissing && endOffsetIsMissing) return null;
        /**
         * FALLBACK
         *   Missing `startOffset`             -> use `0`
         *   Missing `endOffset`               -> use `nextAcceptedTextOffset`
         *
         * RETURNS
         *   Paragraph span covered by the tracked bookmarks in accepted-text offsets.
         *
         * EXAMPLE
         *   end seen, no start in paragraph   -> span began before this paragraph
         *   start seen, no end in paragraph   -> span runs through this paragraph end
         */
        return { startOffset: startOffset ?? 0, endOffset: endOffset ?? nextAcceptedTextOffset };
    }

    /**
     * True when one of the bookmarks reaches into a neighbouring paragraph without
     * covering any of its text, which is how Word grows a bookmark when a paragraph
     * break is inserted at its edge: the start lands at the very end of the previous
     * paragraph, or the end at the very start of the next. The tracker answers it by
     * re-laying the bookmark edges.
     *
     * WHY each bookmark on its own
     *   Word grows one bookmark when a paragraph break is typed at its edge, and
     *   `repairEdges` re-lays each bookmark on its own. A table card lays one bookmark in
     *   each of its cells: read as one range, the bookmark of an empty first or last cell,
     *   which covers no text, reads as an edge on a neighbour although no bookmark grew.
     *
     * EXAMPLE
     *   row card over "Ex. 3" | "OPEN: When was the order delivered?" | (empty), a bookmark a cell
     *   the three as one range    spans 0-5, 0-35, 0-0, ends at 0 of its last paragraph -> true
     *   each bookmark on its own  0-5 | 0-35 | 0-0, each inside its own paragraph      -> false
     */
    edgesNeedMove(modelOf: (paragraph: Element) => ParagraphModel): boolean {
        const modelledParagraphs = this.paragraphElements.map((paragraphElement): ModelledParagraph => ({ paragraphElement, model: modelOf(paragraphElement) }));
        return Array.from(this.bookmarkNames).some((bookmarkName) => new OoxmlBookmarks(this.documentBodyElement, this.paragraphElements, [bookmarkName]).singleBookmarkEdgesNeedMove(modelledParagraphs));
    }

    /** `edgesNeedMove` for the one bookmark of this instance, over the paragraphs' models read once for every bookmark asked. */
    private singleBookmarkEdgesNeedMove(modelledParagraphs: ModelledParagraph[]): boolean {
        /**
         * WHEN `entry.span !== null`
         *   CONSEQUENCE
         *     Keep the entry and narrow `span` to `AcceptedTextSpan`.
         */
        const coveredParagraphs = modelledParagraphs
            .map((modelledParagraph) => ({ ...modelledParagraph, span: this.spanWithin(modelledParagraph.paragraphElement, modelledParagraph.model) }))
            .filter((entry): entry is typeof entry & { span: AcceptedTextSpan } => entry.span !== null);
        /**
         * WHEN fewer than 2 paragraphs are covered
         *   CONSEQUENCE
         *     return false
         *
         * WHY
         *   This check only matters when coverage crosses a paragraph boundary.
         */
        if (coveredParagraphs.length < 2) return false;
        /**
         * EXAMPLE
         *   covered entries at positions 1 and 3 after filtering
         *   coveredParagraphs[0]              -> the one at position 1
         */
        const firstCoveredParagraph = coveredParagraphs[0];
        /**
         * EXAMPLE
         *   coveredParagraphs.length = 3
         *   last index              = 2
         *   entries                 = 0, 1, 2
         *   selected entry          = 2
         */
        const lastCoveredParagraph = coveredParagraphs[coveredParagraphs.length - 1];
        /**
         * TRUE MEANS
         *   The leading covered paragraph is touched only at or after its final accepted
         *   character, so none of its text is covered.
         */
        const firstCoveredParagraphHasNoCoveredText = firstCoveredParagraph.span.startOffset >= firstCoveredParagraph.model.text.length;
        /**
         * TRUE MEANS
         *   The trailing covered paragraph is touched only at its left edge, before any
         *   accepted character, so none of its text is covered.
         */
        const lastCoveredParagraphHasNoCoveredText = lastCoveredParagraph.span.endOffset === 0;
        return firstCoveredParagraphHasNoCoveredText || lastCoveredParagraphHasNoCoveredText;
    }

    /** The names present in `root`, so a caller can list which of a document's bookmarks a paragraph carries. */
    static namesIn(root: Element): string[] {
        return Array.from(root.getElementsByTagNameNS(WORDPROCESSINGML_NS, "bookmarkStart"))
            .map((marker) => OoxmlBookmarks.nameOf(marker))
            .filter((name) => name.length > 0);
    }

    /**
     * The end marker of the paragraph's own bookmark, when its start and its end are
     * both children of the paragraph: where words written into a paragraph with no words
     * left go, so the bookmark covers them.
     *
     * WHY
     *   A paragraph the user emptied keeps its bookmark as a point: a start and an end with
     *   nothing between. Words appended after the end stood outside the bookmark, so every
     *   reader that finds the paragraph by its bookmark found an empty range: the comment
     *   writer looked for "euros" there and Word threw.
     *
     * CASES
     *   - a paragraph bookmark starts and ends among the children -> its end
     *   - none does (no paragraph bookmark, or its start or end
     *     sits outside the paragraph)                            -> null
     *
     * EXAMPLE
     *   <w:p><w:pPr/><w:bookmarkStart w:id="4" w:name="_icon_edge_reload"/><w:bookmarkEnd w:id="4"/></w:p>
     *   -> the bookmarkEnd w:id="4"
     *
     * NULL
     *   No bookmark of the paragraph's own lies inside it; the caller appends, as Word would.
     */
    static paragraphBookmarkEndWithin(paragraph: Element): Element | null {
        const childElements = Array.from(paragraph.childNodes).filter((node): node is Element => node.nodeType === 1);
        const paragraphBookmarkIds = new Set(childElements
            .filter((child) => elementNameWithoutPrefix(child) === "bookmarkStart" && isParagraphBookmarkName(OoxmlBookmarks.nameOf(child)))
            .map((bookmarkStart) => OoxmlBookmarks.idOf(bookmarkStart)));
        return childElements.find((child) => elementNameWithoutPrefix(child) === "bookmarkEnd" && paragraphBookmarkIds.has(OoxmlBookmarks.idOf(child))) ?? null;
    }

    /**
     * Removes every paragraph bookmark of a scheme the active naming retires,
     * from the whole document, so a document restamped under the active scheme
     * carries one paragraph bookmark per paragraph and never two shapes at once.
     *
     * WHEN the active naming retires no prefix (the legacy scheme)
     *   CONSEQUENCE
     *     nothing is removed and the document is untouched.
     *
     * CASES, for where a retired start sits
     *   - inside its paragraph                    -> removed with its end
     *   - at body level between paragraphs        -> removed with its end, wherever the end sits
     *   - inside a table cell or content control  -> removed; the tag search walks every depth
     *   - an end whose start is not retired       -> kept: ends are matched by `w:id` to the removed starts only
     *
     * RETURNS
     *   The removed names in document order; empty when nothing was retired.
     */
    static removeRetiredParagraphBookmarks(documentRoot: Element): string[] {
        const isRetiredName = (bookmarkName: string): boolean => RETIRED_PARAGRAPH_BOOKMARK_PREFIXES.some((retiredPrefix) => bookmarkName.startsWith(retiredPrefix));
        const retiredStartMarkers = Array.from(documentRoot.getElementsByTagNameNS(WORDPROCESSINGML_NS, "bookmarkStart")).filter((marker) => isRetiredName(OoxmlBookmarks.nameOf(marker)));
        const retiredBookmarkIds = new Set(retiredStartMarkers.map((marker) => OoxmlBookmarks.idOf(marker)));
        const retiredEndMarkers = Array.from(documentRoot.getElementsByTagNameNS(WORDPROCESSINGML_NS, "bookmarkEnd")).filter((marker) => retiredBookmarkIds.has(OoxmlBookmarks.idOf(marker)));
        const removedNames = retiredStartMarkers.map((marker) => OoxmlBookmarks.nameOf(marker));
        for (const marker of [...retiredStartMarkers, ...retiredEndMarkers]) marker.parentNode?.removeChild(marker);
        return removedNames;
    }

    /**
     * Which body paragraph each paragraph bookmark names, decided from the
     * `bookmarkStart` markers alone; `bookmarkEnd` is never consulted.
     *
     * RULE
     *   A paragraph bookmark start names the first body paragraph that has text
     *   at or after it.
     *
     * CASES
     *   - start inside a paragraph, text after it        -> that paragraph (the usual shape)
     *   - start inside a paragraph that has no text      -> that paragraph (an empty paragraph keeps its own name)
     *   - start inside a paragraph, after all its text   -> the next body paragraph; Word leaves the start in the
     *                                                       previous paragraph's tail when Enter is pressed at the
     *                                                       bookmark's first character
     *   - start at body level, between paragraphs        -> the next body paragraph; Word writes a bookmark that
     *                                                       begins at a paragraph's first character this way
     *   - start after the last body paragraph            -> names nothing
     *   - start inside an `mc:Fallback` copy             -> ignored, the same copy `bodyParagraphs` skips
     *
     * EXAMPLE
     *   <w:p>A</w:p><w:bookmarkStart w:name="_dioptra_p_x"/><w:p>B</w:p>   -> B owns _dioptra_p_x
     *   <w:p>A<w:bookmarkStart w:name="_dioptra_p_y"/></w:p><w:p>B</w:p>    -> B owns _dioptra_p_y
     *   <w:p><w:bookmarkStart w:name="_dioptra_p_z"/>A</w:p>                 -> A owns _dioptra_p_z
     *
     * RETURNS
     *   The names each paragraph owns, in document order of their starts. A
     *   paragraph that owns none is absent from the map. `isParagraphBookmarkName`
     *   decides which bookmark names count as paragraph bookmarks at all.
     */
    static paragraphBookmarkNamesByOwner(bodyRoot: Element, bodyParagraphs: Element[]): Map<Element, string[]> {
        const bodyParagraphSet = new Set(bodyParagraphs);
        const ownedNamesByParagraph = new Map<Element, string[]>();
        /**
         * TYPE
         *   Names whose start was seen with no paragraph to own it yet: at body level, or in the
         *   tail of the paragraph just closed. The next body paragraph entered takes them.
         */
        const namesWaitingForNextParagraph: string[] = [];
        const assignName = (paragraph: Element, bookmarkName: string): void => {
            const ownedNames = ownedNamesByParagraph.get(paragraph);
            /**
             * CASES
             *   - first name for this paragraph -> start its list
             *   - later name                    -> append, so document order is kept
             */
            if (ownedNames === undefined) ownedNamesByParagraph.set(paragraph, [bookmarkName]);
            else ownedNames.push(bookmarkName);
        };
        const elementChildren = (parent: Element): Element[] => Array.from(parent.childNodes).filter((node): node is Element => node.nodeType === 1);

        /**
         * Walks one body paragraph: counts its text elements and records the paragraph
         * bookmark starts inside it with the count seen before each, then decides each
         * start once the whole paragraph has been read.
         */
        const visitBodyParagraph = (paragraph: Element): void => {
            for (const waitingName of namesWaitingForNextParagraph) assignName(paragraph, waitingName);
            namesWaitingForNextParagraph.length = 0;
            let textElementsSeen = 0;
            const startsInsideParagraph: ParagraphBookmarkStartInsideParagraph[] = [];
            const visitInsideParagraph = (element: Element): void => {
                const elementName = elementNameWithoutPrefix(element);
                /**
                 * WHEN the element is an `mc:Fallback` copy of drawn content
                 *   CONSEQUENCE
                 *     Skip it: its markers duplicate the `mc:Choice` branch already walked.
                 */
                if (elementName === "Fallback") return;
                /**
                 * WHEN the element is a paragraph bookmark start
                 *   CONSEQUENCE
                 *     Record it with the text count so far; the decision waits for the paragraph's end.
                 */
                if (elementName === "bookmarkStart") {
                    const bookmarkName = OoxmlBookmarks.nameOf(element);
                    if (isParagraphBookmarkName(bookmarkName)) startsInsideParagraph.push({ bookmarkName, textElementsBeforeStart: textElementsSeen });
                    return;
                }
                /**
                 * WHEN the element holds paragraph characters (`w:t` or `w:delText`)
                 *   CONSEQUENCE
                 *     Count it. Struck text counts too: a paragraph whose whole text is a tracked
                 *     deletion still owns the bookmark that starts before that text.
                 */
                if (TEXT_TAGS.has(elementName)) {
                    textElementsSeen += 1;
                    return;
                }
                for (const child of elementChildren(element)) visitInsideParagraph(child);
            };
            for (const child of elementChildren(paragraph)) visitInsideParagraph(child);
            for (const start of startsInsideParagraph) {
                const paragraphHasText = textElementsSeen > 0;
                const textFollowsStart = textElementsSeen > start.textElementsBeforeStart;
                /**
                 * CASES
                 *   - no text in the paragraph    -> this paragraph owns the name
                 *   - text after the start        -> this paragraph owns the name
                 *   - text only before the start  -> the start sits in this paragraph's tail: the next paragraph owns it
                 */
                if (!paragraphHasText || textFollowsStart) assignName(paragraph, start.bookmarkName);
                else namesWaitingForNextParagraph.push(start.bookmarkName);
            }
        };

        /** Walks everything outside body paragraphs: body-level starts wait for the next paragraph. */
        const visitOutsideParagraphs = (element: Element): void => {
            if (bodyParagraphSet.has(element)) {
                visitBodyParagraph(element);
                return;
            }
            const elementName = elementNameWithoutPrefix(element);
            if (elementName === "Fallback") return;
            if (elementName === "bookmarkStart") {
                const bookmarkName = OoxmlBookmarks.nameOf(element);
                if (isParagraphBookmarkName(bookmarkName)) namesWaitingForNextParagraph.push(bookmarkName);
                return;
            }
            for (const child of elementChildren(element)) visitOutsideParagraphs(child);
        };
        visitOutsideParagraphs(bodyRoot);
        return ownedNamesByParagraph;
    }

    /**
     * The next unused bookmark id under `root`: one above the largest numeric
     * `w:id` on any `bookmarkStart` or `bookmarkEnd`, which is how Word numbers
     * a new bookmark. Comment and revision ids live in other id spaces and are
     * not consulted.
     *
     * EXAMPLE
     *   ids present = 3, 28, 466   -> 467
     *   no bookmarks at all        -> 1
     */
    static nextBookmarkId(root: Element): number {
        let largestBookmarkId = 0;
        for (const markerTagName of ["bookmarkStart", "bookmarkEnd"]) {
            for (const marker of Array.from(root.getElementsByTagNameNS(WORDPROCESSINGML_NS, markerTagName))) {
                const markerId = Number.parseInt(OoxmlBookmarks.idOf(marker), 10);
                /**
                 * WHEN the id is a number larger than any seen so far
                 *   CONSEQUENCE
                 *     Remember it. A marker without a numeric id cannot collide with the one minted.
                 */
                if (Number.isFinite(markerId) && markerId > largestBookmarkId) largestBookmarkId = markerId;
            }
        }
        return largestBookmarkId + 1;
    }

    private static nameOf(marker: Element): string {
        /**
         * FALLBACK
         *   namespace-aware `name`   -> use it
         *   `w:name`                 -> otherwise use it
         *   missing both             -> `""`
         */
        return marker.getAttributeNS(WORDPROCESSINGML_NS, "name") ?? marker.getAttribute("w:name") ?? "";
    }

    private static idOf(marker: Element): string {
        /**
         * FALLBACK
         *   the marker carries an id  -> use it
         *   it carries none           -> `""`, an id no marker pairs with
         */
        return wordIdOf(marker) ?? "";
    }
}
